import messages from "./messages";

/**
 * Column definitions for the four NAV tables.
 *
 * Headers are built from react-intl messages, so the column arrays are functions of `intl` rather
 * than module constants. Every view memoises the result (react-table re-renders forever if the
 * columns array changes identity on every render).
 *
 * `sortKey` is the string sent to the backend as `sort_by`. Only the keys in
 * AdminNavService.NAV_HISTORY_SORT_COLUMNS are accepted there; anything else falls back to the
 * default sort, so the strings below are spelled exactly as the service spells them. Columns with
 * no `sortKey` render no sort control, because the API cannot sort on them.
 *
 * `width` is a custom key read back through column.render("width"), the house pattern.
 * `numeric` right-aligns a cell; it never changes the value that is rendered.
 */

export function navHistoryColumns(intl) {
  return [
    {
      Header: intl.formatMessage(messages.colFundNo),
      accessor: "fund_no",
      sortKey: "fund_no",
      width: 100
    },
    {
      Header: intl.formatMessage(messages.colFundCode),
      accessor: "fund_code",
      sortKey: "fund_code",
      width: 130
    },
    {
      Header: intl.formatMessage(messages.colFundName),
      accessor: "fund_name",
      width: 280
    },
    {
      Header: intl.formatMessage(messages.colNavDate),
      accessor: "nav_date",
      sortKey: "nav_date",
      width: 120
    },
    {
      Header: intl.formatMessage(messages.colNavPerUnit),
      accessor: "navperunit",
      sortKey: "navperunit",
      width: 140,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colPriceChg),
      accessor: "price_chg",
      sortKey: "price_chg",
      width: 120,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colPercentChg),
      accessor: "percent_chg",
      sortKey: "percent_chg",
      width: 130,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colReturnType),
      accessor: "return_type",
      sortKey: "return_type",
      width: 120
    },
    {
      Header: intl.formatMessage(messages.colNavEffdateRaw),
      accessor: "nav_effdate_raw",
      width: 160
    },
    {
      Header: intl.formatMessage(messages.colSourceTypeNav),
      accessor: "source_type_nav",
      sortKey: "source_type_nav",
      width: 150
    },
    {
      Header: intl.formatMessage(messages.colSyncBy),
      accessor: "sync_by",
      sortKey: "sync_by",
      width: 110
    },
    {
      Header: intl.formatMessage(messages.colCreatedAt),
      accessor: "created_at",
      sortKey: "created_at",
      width: 200
    },
    {
      Header: intl.formatMessage(messages.colUpdatedAt),
      accessor: "updated_at",
      sortKey: "updated_at",
      width: 200
    }
  ];
}

export function topHoldingColumns(intl) {
  return [
    {
      Header: intl.formatMessage(messages.colFundNo),
      accessor: "fund_no",
      width: 100
    },
    {
      Header: intl.formatMessage(messages.colFundName),
      accessor: "fund_name",
      width: 260
    },
    {
      Header: intl.formatMessage(messages.colSeq),
      accessor: "seq",
      width: 80
    },
    {
      Header: intl.formatMessage(messages.colListTh),
      accessor: "list_th",
      width: 300
    },
    {
      Header: intl.formatMessage(messages.colListEng),
      accessor: "list_eng",
      width: 300
    },
    {
      Header: intl.formatMessage(messages.colAmountInv),
      accessor: "amount_inv",
      width: 150,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colPercentInv),
      accessor: "percent_inv",
      width: 130,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colPercentInvRaw),
      accessor: "percent_inv_raw",
      width: 160,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colValueDate),
      accessor: "value_date",
      width: 120
    },
    {
      Header: intl.formatMessage(messages.colSyncBy),
      accessor: "sync_by",
      width: 110
    },
    {
      Header: intl.formatMessage(messages.colCreatedAt),
      accessor: "created_at",
      width: 200
    },
    {
      Header: intl.formatMessage(messages.colUpdatedAt),
      accessor: "updated_at",
      width: 200
    }
  ];
}

export function syncStateColumns(intl) {
  return [
    {
      Header: intl.formatMessage(messages.colJobName),
      accessor: "job_name",
      width: 220
    },
    {
      Header: intl.formatMessage(messages.colLastRunAt),
      accessor: "last_run_at",
      width: 210
    },
    {
      Header: intl.formatMessage(messages.colLastSuccessAt),
      accessor: "last_success_at",
      width: 210
    },
    {
      Header: intl.formatMessage(messages.colLastStatus),
      accessor: "last_status",
      width: 130
    },
    {
      Header: intl.formatMessage(messages.colRowsAffected),
      accessor: "rows_affected",
      width: 140,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colDetail),
      accessor: "detail",
      width: 400
    }
  ];
}

export function coverageSummaryColumns(intl) {
  return [
    {
      Header: intl.formatMessage(messages.colFundNo),
      accessor: "fund_no",
      width: 100
    },
    {
      Header: intl.formatMessage(messages.colFundCode),
      accessor: "fund_code",
      width: 140
    },
    {
      Header: intl.formatMessage(messages.colFundName),
      accessor: "fund_name",
      width: 300
    },
    {
      Header: intl.formatMessage(messages.colRowCount),
      accessor: "row_count",
      width: 130,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colEarliestNavDate),
      accessor: "earliest_nav_date",
      width: 170
    },
    {
      Header: intl.formatMessage(messages.colLatestNavDate),
      accessor: "latest_nav_date",
      width: 170
    },
    {
      Header: intl.formatMessage(messages.colLatestNavPerUnit),
      accessor: "latest_navperunit",
      width: 190,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colLastSyncBy),
      accessor: "last_sync_by",
      width: 130
    }
  ];
}

/**
 * One row per fund, holding that fund's newest NAV row.
 *
 * The values come from the per-fund summary read, which picks every one of them out of the SAME
 * newest row (`ARRAY_AGG(... ORDER BY nav_date DESC)[1]` per column), so a row here is a real NAV
 * record rather than a mix of columns from different dates.
 *
 * No `sortKey` anywhere: the summary read is unpaginated and returns every fund in one response,
 * so this table sorts in the browser instead of asking the backend to.
 */
export function latestByFundColumns(intl) {
  return [
    {
      Header: intl.formatMessage(messages.colFundNo),
      accessor: "fund_no",
      width: 100
    },
    {
      Header: intl.formatMessage(messages.colFundCode),
      accessor: "fund_code",
      width: 150
    },
    {
      Header: intl.formatMessage(messages.colFundName),
      accessor: "fund_name",
      width: 260
    },
    {
      Header: intl.formatMessage(messages.colLatestNavDate),
      accessor: "latest_nav_date",
      width: 140
    },
    {
      Header: intl.formatMessage(messages.colLatestNavPerUnit),
      accessor: "latest_navperunit",
      width: 170,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colLatestPriceChg),
      accessor: "latest_price_chg",
      width: 120,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colLatestPercentChg),
      accessor: "latest_percent_chg",
      width: 130,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colLatestReturnType),
      accessor: "latest_return_type",
      width: 140
    },
    {
      Header: intl.formatMessage(messages.colLatestSourceTypeNav),
      accessor: "latest_source_type_nav",
      width: 160
    },
    {
      Header: intl.formatMessage(messages.colRowCount),
      accessor: "row_count",
      width: 120,
      numeric: true
    },
    {
      Header: intl.formatMessage(messages.colLastSyncBy),
      accessor: "last_sync_by",
      width: 130
    }
  ];
}
